import { useEffect, useRef, useState } from "react";
import "@/components/site/ondose-journey.css";
import "@/lib/ondose-journey";
import { useSamples } from "@/components/site/SamplesContext";

const MEDS = [["Thyroid · Thyronorm", "Dr. Anil", "Sai Krishna Hospital"], ["BP · Amlodipine", "Dr. Ramesh", "Sri Sai Hospital"], ["Sugar · Metformin", "Dr. Kavitha", "Aditya Clinic"]];
const DATES = Array.from({ length: 30 }, (_, i) => i + 1);

const Step = ({ n, chip, title, caption, children }) => (
  <li className="odj__step" data-testid={`journey-step-${n}`}>
    <span className="odj__chip">{chip}</span>
    <div className="odj__stage">{children}</div>
    <div className="odj__node" aria-hidden="true">{n}</div>
    <h3>{title}</h3>
    <p className="odj__caption">{caption}</p>
  </li>
);

function CallCard({ on, msg, ready, onClick }) {
  return (
    <div className="odj__call odj__surface">
      <div className="odj__call-top"><span className="odj__tiny-dot" aria-hidden="true"></span> DAILY REMINDER <span className="odj__sample">SAMPLE</span></div>
      <svg className="odj__phone-icon" viewBox="0 0 48 48" fill="none" aria-hidden="true"><path d="M13 7l6 9-5 5c4 7 7 10 14 14l5-5 9 6c-1 5-4 8-9 7C17 40 8 31 5 15 4 10 8 7 13 7Z" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" /></svg>
      <p className="odj__call-title">One call.<br />Three doctors.</p>
      <p className="odj__muted">To Lakshmi garu</p>
      <p className="odj__telugu font-te" lang="te">నమస్తే లక్ష్మి గారూ…</p>
      <div className="odj__wave" aria-hidden="true">{Array.from({ length: 15 }, (_, i) => <i key={i}></i>)}</div>
      <button className="odj__play" type="button" aria-label={on ? "Stop sample call" : "Play six-second sample call"} aria-pressed={on} disabled={!ready} onClick={onClick} data-testid="journey-play-btn"><span className="odj__play-symbol" aria-hidden="true">{on ? "■" : "▶"}</span><span className="odj__play-label">{on ? "Stop sample" : "Hear sample · 6 sec"}</span></button>
      <p className="odj__status" role="status" aria-live="polite" data-testid="journey-status">{ready ? msg : "Sample voice is being prepared."}</p>
    </div>
  );
}

function Journey({ on, msg, ready, onClick }) {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    window.initOnDoseJourney();
    return () => root.odjDestroy?.();
  }, []);
  return (
    <section id="discover" className={`odj${on ? " odj--playing" : ""}`} aria-labelledby="odj-heading" ref={ref} data-testid="section-discover">
      <div className="odj__inner">
        <header className="odj__header">
          <p className="odj__eyebrow">DISCOVER</p>
          <h2 id="odj-heading">How it works</h2>
          <p>Follow one patient, <strong>Lakshmi garu</strong>, from the doctor’s desk to her daily reminder.</p>
        </header>
        <ol className="odj__journey">
          <Step n={1} chip="Day 1 · 11:00 AM" title="Doctor prescribes as usual" caption="Nothing changes for the doctor.">
            <div className="odj__paper odj__surface">
              <div className="odj__paper-top"><span aria-hidden="true">℞</span><span className="odj__sample">SAMPLE</span></div>
              <p className="odj__patient">Lakshmi garu</p>
              <p className="odj__muted">Combined medicine list</p>
              <ul className="odj__medicines">{MEDS.map(([m, d, h]) => <li key={m}><strong>{m}</strong><span>{d}</span><small>{h}</small></li>)}</ul>
            </div>
          </Step>
          <Step n={2} chip="Day 1 · 6:00 PM" title="Staff scan & confirm" caption="Staff check the medicines and timings.">
            <div className="odj__scanner odj__surface">
              <div className="odj__scanner-top"><span className="odj__tiny-dot" aria-hidden="true"></span><strong>Scan &amp; confirm</strong></div>
              <div className="odj__scan-preview" aria-hidden="true"><span>℞</span><strong>Lakshmi garu</strong><i></i><i></i><i></i></div>
              <ul className="odj__confirmed">{["Thyronorm", "Amlodipine", "Metformin"].map((m) => <li key={m}><strong>{m}</strong><span>✓ Confirmed</span></li>)}</ul>
              <p className="odj__scanner-note">Checked by your staff</p>
            </div>
          </Step>
          <Step n={3} chip="Day 2 · 8:00 AM" title="Patient gets a daily call" caption="In her language. On any phone. No app."><CallCard on={on} msg={msg} ready={ready} onClick={onClick} /></Step>
          <Step n={4} chip="Day 25 · 10:00 AM" title="Refill & follow-up" caption="A reminder before medicines run out.">
            <div className="odj__calendar odj__surface">
              <div className="odj__calendar-title"><span aria-hidden="true">▦</span><strong>Refill calendar</strong></div>
              <p className="odj__muted">Illustrative 30-day supply</p>
              <div className="odj__dates" aria-hidden="true">{DATES.map((d) => <span key={d} className={d === 25 ? "odj__today" : d === 30 ? "odj__due" : undefined}>{d}</span>)}</div>
              <div className="odj__refill"><span aria-hidden="true">↗</span><div><strong>BP refill due in 5 days</strong><p>Visit Dr. Ramesh</p></div></div>
            </div>
          </Step>
        </ol>
        <ul className="odj__results" aria-label="Benefits">
          <li><span className="odj__result-icon" aria-hidden="true">◷</span><div><strong>Every dose reminded</strong><p>A call at each scheduled medicine time</p></div></li>
          <li><span className="odj__result-icon" aria-hidden="true">↗</span><div><strong>Follow-ups remembered</strong><p>A reminder to return before medicines run out</p></div></li>
          <li><span className="odj__result-icon" aria-hidden="true">✓</span><div><strong>Confirm once. Calls follow.</strong><p>Staff confirm the schedule; reminders run automatically</p></div></li>
        </ul>
        <p className="odj__disclaimer">Illustrative patient journey. Names and hospitals are samples. Calls follow the schedule your staff confirm.</p>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  const { samples, clipPlaying, clip, playClip } = useSamples();
  const [msg, setMsg] = useState("A short preview of the morning call");
  const on = clipPlaying && clip === "merged";
  const was = useRef(false);
  useEffect(() => {
    if (was.current && !on) setMsg("Preview finished");
    was.current = on;
  }, [on]);
  const ready = !!samples.find((s) => s.key === "merged")?.audio_url;
  const click = () => {
    setMsg(on ? "" : "Loading sample…");
    playClip("merged", 6).then(() => setMsg("")).catch((e) => setMsg(`Could not play the sample (${e?.name || "error"}). Please try again.`));
  };
  return <Journey on={on} msg={msg} ready={ready} onClick={click} />;
}
